import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowRight, ArrowLeft, Upload, Palette, Eye, Printer, Truck, Instagram, MessageCircle, FileText, Check, Ruler, Clock, Shirt } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import Layout from '../components/layout/Layout';
import { COLLECTIONS, PRODUCT_OPTIONS, SIZE_CHART, BRAND } from '../data/mock';
import BeforeAfterSection from '../components/cro/BeforeAfterSection';
import ProductPageEnhancements from '../components/cro/ProductPageEnhancements';
import TrustBadges from '../components/cro/TrustBadges';
import { trackProductView, trackWhatsAppClick, trackInstagramClick } from '../lib/analytics';

const CategoryDetailPage = () => {
  const { categoryId } = useParams();
  const collection = COLLECTIONS.find(c => c.id === categoryId);

  // Hooks must always be called before any early return (Rules of Hooks)
  React.useEffect(() => {
    if (collection) {
      trackProductView(collection.id, collection.title);
    }
  }, [collection]);

  const handleWhatsApp = () => {
    trackWhatsAppClick('product_page');
    const msg = `Hi AuraPrintiqo! 👋\n\nI want to order from the ${collection.title}.\n\nCan you guide me?`;
    window.open(`https://wa.me/918889616972?text=${encodeURIComponent(msg)}`, '_blank', 'noopener,noreferrer');
  };

  const handleInstagram = () => {
    trackInstagramClick('product_page');
    window.open(`https://ig.me/m/aura_printiqo`, '_blank', 'noopener,noreferrer');
  };

  if (!collection) {
    return (
      <Layout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Collection Not Found</h1>
            <Link to="/collections">
              <Button>Back to Collections</Button>
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  const customizationSteps = [
    {
      icon: Upload,
      title: "Upload Your Photo",
      description: "Send us a clear, well-lit photo via Instagram DM or WhatsApp. Front-facing shots work best!"
    },
    {
      icon: Palette,
      title: "Choose Your Product",
      description: "Select from our premium T-shirts or cozy hoodies. Pick your size and preferred color."
    },
    {
      icon: Eye,
      title: "AI Artwork Generation",
      description: "Our AI transforms your photo into stunning anime-style artwork within 24 hours."
    },
    {
      icon: Check,
      title: "Preview & Approve",
      description: "We share your custom design for approval. Request up to 2 revisions if needed."
    },
    {
      icon: Printer,
      title: "Premium Printing",
      description: "Your approved design is printed using high-quality DTG printing on premium fabric."
    },
    {
      icon: Truck,
      title: "Delivered to You",
      description: "Your custom piece is carefully packed and shipped directly to your doorstep."
    }
  ];

  // Get category-specific descriptions for before/after
  const getBeforeAfterDescriptions = () => {
    if (categoryId === 'pet-anime') {
      return {
        before: { title: "Your Pet Photo", desc: "Send us your pet's cutest photo" },
        after: { title: "AI Anime Artwork", desc: "Transformed into anime style" },
        final: { title: "Printed on T-Shirt", desc: "Ready to wear and flaunt" }
      };
    } else if (categoryId === 'couple-anime') {
      return {
        before: { title: "Your Couple Photo", desc: "Share your favorite together moment" },
        after: { title: "Ghibli-Style Art", desc: "Transformed into romantic art" },
        final: { title: "Printed on Hoodie", desc: "Matching couple merchandise" }
      };
    } else {
      return {
        before: { title: "Your Team Photo", desc: "Send your team or logo concept" },
        after: { title: "Custom Design", desc: "Professional branded artwork" },
        final: { title: "Bulk Order Ready", desc: "Premium quality for your team" }
      };
    }
  };

  const descriptions = getBeforeAfterDescriptions();

  return (
    <Layout>
      {/* Trust Badges - right at top of product page */}
      <TrustBadges />
      {/* Hero Section */}
      <section className="py-12 sm:py-20 bg-gradient-to-br from-[hsl(var(--cream))] via-white to-[hsl(var(--peach))]/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Breadcrumb */}
          <Link 
            to="/collections" 
            className="inline-flex items-center gap-2 text-gray-600 hover:text-[hsl(350,70%,55%)] transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Collections
          </Link>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block px-4 py-2 bg-[hsl(350,70%,65%)]/10 rounded-full text-sm font-medium text-[hsl(350,70%,55%)] mb-4">
                {collection.products.length} Product Options
              </span>
              <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
                {collection.title}
              </h1>
              <p className="text-lg text-gray-600 leading-relaxed mb-8">
                {collection.description}
              </p>
              
              <div className="flex flex-wrap gap-3 mb-8">
                {collection.products.map((product) => (
                  <span 
                    key={product}
                    className="px-4 py-2 bg-white rounded-xl text-sm font-medium text-gray-700 shadow-soft"
                  >
                    {product}
                  </span>
                ))}
              </div>

              <div className="flex items-center gap-4">
                <span className="text-2xl font-bold text-gray-900">{collection.priceRange}</span>
              </div>
            </div>

            <div className="relative">
              <img
                src={collection.image}
                alt={collection.title}
                className="w-full h-[400px] sm:h-[500px] object-cover rounded-3xl shadow-soft-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Before/After Preview Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-2 bg-[hsl(var(--mint))]/50 rounded-full text-sm font-medium text-[hsl(var(--mint-dark))] mb-4">
              Customization Preview
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              See the Transformation
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              From your original photo to AI artwork to the final printed product
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Before - Customer Photo */}
            <Card className="border-0 shadow-soft overflow-hidden">
              <div className="relative h-64">
                <img
                  src={collection.beforeImage}
                  alt="Original photo"
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-4 left-4">
                  <span className="px-3 py-1.5 bg-gray-900/80 text-white rounded-full text-sm font-medium">
                    1. Your Photo
                  </span>
                </div>
              </div>
              <CardContent className="p-6 text-center">
                <h3 className="font-semibold text-gray-900 mb-2">{descriptions.before.title}</h3>
                <p className="text-sm text-gray-600">{descriptions.before.desc}</p>
              </CardContent>
            </Card>

            {/* After - AI Art */}
            <Card className="border-0 shadow-soft overflow-hidden ring-2 ring-[hsl(350,70%,65%)]/30">
              <div className="relative h-64">
                <img
                  src={collection.afterImage}
                  alt="AI generated artwork"
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-4 left-4">
                  <span className="px-3 py-1.5 bg-gradient-to-r from-[hsl(350,70%,65%)] to-[hsl(20,90%,70%)] text-white rounded-full text-sm font-medium">
                    2. AI Artwork
                  </span>
                </div>
              </div>
              <CardContent className="p-6 text-center bg-gradient-to-b from-[hsl(var(--peach))]/30 to-white">
                <h3 className="font-semibold text-gray-900 mb-2">{descriptions.after.title}</h3>
                <p className="text-sm text-gray-600">{descriptions.after.desc}</p>
              </CardContent>
            </Card>

            {/* Final Product */}
            <Card className="border-0 shadow-soft overflow-hidden">
              <div className="relative h-64">
                <img
                  src={collection.finalImage}
                  alt="Final printed product"
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-4 left-4">
                  <span className="px-3 py-1.5 bg-[hsl(var(--mint-dark))] text-white rounded-full text-sm font-medium">
                    3. Final Print
                  </span>
                </div>
              </div>
              <CardContent className="p-6 text-center">
                <h3 className="font-semibold text-gray-900 mb-2">{descriptions.final.title}</h3>
                <p className="text-sm text-gray-600">{descriptions.final.desc}</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* How Customization Works */}
      <section className="py-16 bg-gradient-to-b from-[hsl(var(--cream))] to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-2 bg-[hsl(var(--lavender))]/50 rounded-full text-sm font-medium text-[hsl(var(--lavender-dark))] mb-4">
              Step by Step
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              How Customization Works
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              A simple 6-step process to get your custom {collection.title.toLowerCase()}
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {customizationSteps.map((step, index) => (
              <Card key={index} className="border-0 shadow-soft hover:shadow-soft-lg transition-all duration-300 hover-lift">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[hsl(350,70%,65%)]/10 to-[hsl(20,90%,70%)]/10 flex items-center justify-center">
                        <step.icon className="w-6 h-6 text-[hsl(350,70%,60%)]" />
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="w-6 h-6 rounded-full bg-gradient-to-br from-[hsl(350,70%,65%)] to-[hsl(20,90%,70%)] text-white text-xs font-bold flex items-center justify-center">
                          {index + 1}
                        </span>
                        <h3 className="font-semibold text-gray-900">{step.title}</h3>
                      </div>
                      <p className="text-sm text-gray-600 leading-relaxed">{step.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Product Options */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-2 bg-[hsl(var(--coral-light))]/50 rounded-full text-sm font-medium text-[hsl(var(--coral))] mb-4">
              Product Details
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Product Options
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            {/* T-Shirt Card */}
            <Card className="border-0 shadow-soft overflow-hidden">
              <CardContent className="p-8">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 rounded-2xl bg-[hsl(var(--mint))] flex items-center justify-center">
                    <Shirt className="w-7 h-7 text-[hsl(var(--mint-dark))]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">{PRODUCT_OPTIONS.tshirt.name}</h3>
                    <p className="text-gray-500 text-sm">{PRODUCT_OPTIONS.tshirt.fabric}</p>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <Ruler className="w-5 h-5 text-gray-400" />
                    <span className="text-gray-600">Sizes: {PRODUCT_OPTIONS.tshirt.sizes.join(', ')}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Palette className="w-5 h-5 text-gray-400" />
                    <span className="text-gray-600">Colors: {PRODUCT_OPTIONS.tshirt.colors.join(', ')}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Clock className="w-5 h-5 text-gray-400" />
                    <span className="text-gray-600">Delivery: {PRODUCT_OPTIONS.tshirt.delivery}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Hoodie Card */}
            <Card className="border-0 shadow-soft overflow-hidden">
              <CardContent className="p-8">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 rounded-2xl bg-[hsl(var(--lavender))] flex items-center justify-center">
                    <Shirt className="w-7 h-7 text-[hsl(var(--lavender-dark))]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">{PRODUCT_OPTIONS.hoodie.name}</h3>
                    <p className="text-gray-500 text-sm">{PRODUCT_OPTIONS.hoodie.fabric}</p>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <Ruler className="w-5 h-5 text-gray-400" />
                    <span className="text-gray-600">Sizes: {PRODUCT_OPTIONS.hoodie.sizes.join(', ')}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Palette className="w-5 h-5 text-gray-400" />
                    <span className="text-gray-600">Colors: {PRODUCT_OPTIONS.hoodie.colors.join(', ')}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Clock className="w-5 h-5 text-gray-400" />
                    <span className="text-gray-600">Delivery: {PRODUCT_OPTIONS.hoodie.delivery}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Size Chart */}
          <Card className="border-0 shadow-soft">
            <CardContent className="p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                <Ruler className="w-5 h-5 text-[hsl(350,70%,60%)]" />
                Size Chart (in inches)
              </h3>
              
              <div className="grid md:grid-cols-2 gap-8">
                {/* T-Shirt Size Chart */}
                <div>
                  <h4 className="font-semibold text-gray-800 mb-4">T-Shirt</h4>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Size</TableHead>
                        <TableHead>Chest</TableHead>
                        <TableHead>Length</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {SIZE_CHART.tshirt.map((row) => (
                        <TableRow key={row.size}>
                          <TableCell className="font-medium">{row.size}</TableCell>
                          <TableCell>{row.chest}"</TableCell>
                          <TableCell>{row.length}"</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {/* Hoodie Size Chart */}
                <div>
                  <h4 className="font-semibold text-gray-800 mb-4">Hoodie</h4>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Size</TableHead>
                        <TableHead>Chest</TableHead>
                        <TableHead>Length</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {SIZE_CHART.hoodie.map((row) => (
                        <TableRow key={row.size}>
                          <TableCell className="font-medium">{row.size}</TableCell>
                          <TableCell>{row.chest}"</TableCell>
                          <TableCell>{row.length}"</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* ── AI Transformation Preview ── */}
      {collection.beforeImage && (
        <BeforeAfterSection
          beforeImage={collection.beforeImage}
          afterImage={collection.afterImage}
          finalImage={collection.finalImage}
          title={`Your ${descriptions.before.title} → Anime Art`}
          subtitle={`See how we transform ${descriptions.before.desc.toLowerCase()} into wearable artwork`}
        />
      )}

      {/* ── How It Works + Delivery Info + FAQ ── */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <ProductPageEnhancements collectionTitle={collection.title} />
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-br from-[hsl(350,70%,65%)] to-[hsl(20,90%,65%)]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Ready to Get Your Custom {collection.title.split(' ')[0]} Print?
          </h2>
          <p className="text-white/90 text-lg mb-10 max-w-2xl mx-auto">
            Order now through Instagram or WhatsApp and get your unique anime-style print delivered!
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" onClick={handleInstagram} className="w-full sm:w-auto bg-white text-gray-900 hover:bg-gray-100 px-8 shadow-lg">
              <Instagram className="w-5 h-5 mr-2" />
              Order via Instagram
            </Button>
            <Button size="lg" onClick={handleWhatsApp} className="w-full sm:w-auto bg-green-500 hover:bg-green-600 text-white px-8 shadow-lg">
              <MessageCircle className="w-5 h-5 mr-2" />
              Order via WhatsApp
            </Button>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="w-full sm:w-auto border-2 border-white text-white hover:bg-white/10 px-8">
                <FileText className="w-5 h-5 mr-2" />
                Fill Order Form
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default CategoryDetailPage;
